import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  DEPOSITS_GROUP_BY_OPTIONS,
  TRADES_GROUP_BY_OPTIONS,
} from "../utils/consts";
import { Deposit, Grouping, RawData, Trade } from "../types";
import { ReportTypes } from "../ReportsDashboard";
import { getTradeGroupOptions } from "../utils/tradeUtils";
import { getDepositGroupOptions } from "../utils/depositUtils";

interface GroupByControlsProps<T> {
  groupings: Grouping[];
  data?: RawData<T>;
  addGrouping: () => void;
  updateGrouping: (idx: number, changes: Partial<Grouping>) => void;
  removeGrouping: (idx: number) => void;
  reportType: ReportTypes;
}

const GroupByControls = <T,>({
  data,
  groupings,
  addGrouping,
  updateGrouping,
  removeGrouping,
  reportType,
}: GroupByControlsProps<T>) => {
  // Helper to get group options for a grouping
  function getGroupOptions(g: Grouping) {
    if (!data) return [];

    switch (reportType) {
      case ReportTypes.Trades:
        return getTradeGroupOptions(data as RawData<Trade>, g);
      case ReportTypes.Deposits:
        return getDepositGroupOptions(data as RawData<Deposit>, g);

      default:
        return [];
    }
  }

  const renderGroupByOptions = () => {
    switch (reportType) {
      case ReportTypes.Trades:
        return TRADES_GROUP_BY_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.name}
          </MenuItem>
        ));
      case ReportTypes.Deposits:
        return DEPOSITS_GROUP_BY_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.name}
          </MenuItem>
        ));
      default:
        return [];
    }
  };

  return (
    <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {groupings.map((g, idx) => {
        const groupOptions = getGroupOptions(g);
        return (
          <Box key={idx} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Group By</InputLabel>
              <Select
                value={g.groupBy}
                label="Group By"
                onChange={(e) => {
                  const value = e.target.value as Grouping["groupBy"];
                  updateGrouping(idx, {
                    groupBy: value,
                    groupFilter: [],
                    timeGroup: value === "time" ? "month" : undefined,
                  });
                }}
              >
                {renderGroupByOptions()}
              </Select>
            </FormControl>
            {g.groupBy === "time" ? (
              <FormControl sx={{ flex: 1 }} fullWidth>
                <InputLabel>Time Group</InputLabel>
                <Select
                  value={g.timeGroup}
                  label="Time Group"
                  onChange={(e) =>
                    updateGrouping(idx, {
                      timeGroup: e.target.value as Grouping["timeGroup"],
                    })
                  }
                  fullWidth
                >
                  <MenuItem value="month">Months</MenuItem>
                  <MenuItem value="week">Weeks</MenuItem>
                  <MenuItem value="day">Days</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <FormControl sx={{ flex: 1 }}>
                <Autocomplete
                  multiple
                  options={groupOptions}
                  value={g.groupFilter}
                  onChange={(_, newValue) =>
                    updateGrouping(idx, { groupFilter: newValue })
                  }
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Filter by ${g.groupBy}`}
                      placeholder="Search..."
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) => {
                    const maxVisible = 2;
                    return [
                      ...value.slice(0, maxVisible).map((option, index) => (
                        <Chip
                          label={
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: 120,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                paddingRight: 1,
                              }}
                            >
                              {option}
                            </span>
                          }
                          {...getTagProps({ index })}
                          key={option}
                          sx={{ marginRight: "1px", maxWidth: 180 }}
                        />
                      )),
                      value.length > maxVisible ? (
                        <Chip
                          key="more"
                          label={`+${value.length - maxVisible}`}
                          style={{ maxWidth: 60 }}
                          sx={{ marginRight: "1px" }}
                        />
                      ) : null,
                    ];
                  }}
                  sx={{
                    "& .MuiAutocomplete-inputRoot": {
                      flexWrap: "wrap",
                      maxHeight: "120px",
                      overflowY: "auto",
                    },
                    "& .MuiAutocomplete-tag": {
                      maxWidth: "180px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: 1,
                    },
                  }}
                />
              </FormControl>
            )}
            {
              <IconButton onClick={addGrouping} color="primary" sx={{ mr: -2 }}>
                <AddIcon />
              </IconButton>
            }
            {
              <IconButton
                onClick={() => removeGrouping(idx)}
                color="error"
                sx={{ ml: 1 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            }
          </Box>
        );
      })}
    </Box>
  );
};

export default GroupByControls;
